import { useForm, SubmitHandler, Controller, SubmitErrorHandler } from "react-hook-form";
import styles from "./index.module.scss";
import React, { useState } from "react";
import { useAuthRecovery } from "@/domains/auth/hooks/user-auth-recovery";
import { PhoneInput } from "@/components/UI/PhoneInput/PhoneInput";
import { useStore } from "@/hooks/use-store";
import { showToast } from "@/utils/show-toast";
import { useTranslations } from "next-intl";
import ToggleEmailPhone from "@/components/UI/ToggleEmailPhone";
import cx from "classnames";

interface ShippingField {
  login: string;
  country: string;
}

interface RecoveryPasswordProps {
  onShowTab: (name: string) => void;
}

export default function RecoveryPassword({ onShowTab }: RecoveryPasswordProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<ShippingField>({
    mode: "all",
  });

  const { modal } = useStore();
  const { loading, onMutateAuth, renderErrorMessage, error: authError } = useAuthRecovery();
  const t = useTranslations("ModalRecovery");
  const tValidation = useTranslations("Validation");
  const [authType, setAuthType] = useState<"phone" | "email">("phone");

  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const [country, setCountry] = useState<string>("US");

  const onSubmit: SubmitHandler<ShippingField> = async () => {
    try {
      const login = authType === "phone" ? phoneValue.replace("+", "") : emailValue.trim();

      const res = await onMutateAuth({
        login,
        type: authType,
        countryCode: country.toUpperCase(),
      });

      if (res?.success) {
        showToast({ type: "success", message: t("successSend") });

        modal.closeModal();
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      }
    }
  };

  const onInvalid: SubmitErrorHandler<ShippingField> = (_errors) => {
    console.log("Validation errors: " + JSON.stringify(_errors));
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void handleSubmit(onSubmit, onInvalid)();
  };

  const handleCountryChange = (value: string) => {
    if (value) {
      setCountry(value);
    }
  };

  return (
    <div className={styles.form_wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>{t("title")}</div>
      </div>

      <ToggleEmailPhone
        onChange={(value) => {
          setAuthType(value);
          setValue("login", value === "phone" ? phoneValue : emailValue);
        }}
      />

      <form className={styles.form} onSubmit={handleFormSubmit}>
        <div className={styles.form__field}>
          {authType === "phone" && (
            <label className={cx(styles.label, styles.phone_label)}>
              <Controller
                name="login"
                control={control}
                rules={{ required: tValidation("required") }}
                render={({ field: { onChange } }) => (
                  <PhoneInput
                    className={styles.phone_input}
                    autoComplete={"off"}
                    value={phoneValue}
                    onChange={(_phone, _country) => {
                      setPhoneValue(_phone);
                      onChange(_phone);
                      handleCountryChange(_country);
                    }}
                    label={false}
                  />
                )}
              />
            </label>
          )}

          {authType === "email" && (
            <label className={styles.label}>
              <input
                {...register("login", {
                  required: tValidation("required"),
                  onChange: (e) => setEmailValue(e.target.value),
                })}
                className={styles.input}
                type="email"
                value={emailValue}
                placeholder=""
              />
              <span className={styles.label_value}>E-mail</span>
            </label>
          )}

          {errors.login && <div className={styles.message}>{errors.login.message}</div>}
          <div className={styles.message}>{renderErrorMessage("login", authError || [])}</div>
        </div>

        <div className={styles.form__field}>
          <button className={styles.btn_submit} type="submit" disabled={loading}>
            {loading ? t("submit_wait") : t("submit")}
          </button>
        </div>
      </form>

      <button className={styles.btn_back} onClick={() => onShowTab("login")}>
        {t("login")}
      </button>
    </div>
  );
}
